import React from "react";
import { Modal } from "@pd/pd-ui-kit";
import { useTranslation } from "react-i18next";

import ContractTemplatesForm from "~/pages/Editor/ContractTemplates/modals/ContractTemplatesForm";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import { ContractTemplateTableRow } from "~/pages/Editor/ContractTemplates/model/tableAdapter";
import {
  useCreateContractTemplateMutation,
  useUpdateContractTemplateMutation,
} from "~/schemas/generated/graphql";
import { useNotify } from "~/shared/hooks/useNotify";

const formId = "ContractTemplatesForm";

interface IDescription {
  label: string;
  value: string;
}

interface IContractTemplateFormValues {
  description: IDescription;
  title: string;
  fileId: string;
}

interface ContractTemplatesModalProps {
  reFetch: () => void;
  contracts: ContractTemplateTableRow[];
}

const ContractTemplatesModal = ({
  reFetch,
  contracts,
}: ContractTemplatesModalProps) => {
  const { t } = useTranslation();
  const { openModal, currentId } = useGetLocationData();
  const setData = useSetLocationData(true);
  const isOpenCreateModal = openModal === "Create";
  const isOpenEditModal = openModal === "Edit";
  const { notifySuccess, notifyError } = useNotify();

  const currentContract = contracts.find((item) => item.id === currentId);

  // Handlers
  const closeModal = () =>
    setData("openModal", "", "currentId", "", "fileId", "");

  const [setContractTemplate] = useCreateContractTemplateMutation({
    onCompleted: () => {
      reFetch();
      notifySuccess(t("contractTemplates.catalogTemplate"));
      closeModal();
    },
    onError: (error) => {
      notifyError(error.message);
    },
  });
  const [updateContractTemplate] = useUpdateContractTemplateMutation({
    onCompleted: () => {
      reFetch();
      notifySuccess(t("contractTemplates.editTemplate"));
      closeModal();
    },
    onError: (error) => {
      notifyError(error.message);
    },
  });

  const onSubmit = (prop: IContractTemplateFormValues) => {
    if (currentId) {
      updateContractTemplate({
        variables: {
          updateContractTemplateId: currentId,
          name: prop.title,
          // Отправляем FileId, только если файл был заменён на новый:
          // иначе бэкенд пытается повторно привязать уже прикреплённый
          // файл как черновик и падает с ENOENT.
          ...(prop.fileId && prop.fileId !== currentContract?.FileId
            ? { fileId: prop.fileId }
            : {}),
          active: prop.description.value !== "inactive",
        },
      });
    } else {
      setContractTemplate({
        variables: {
          name: prop.title,
          fileId: prop.fileId,
          active: prop.description.value !== "inactive",
        },
      });
    }
  };

  return (
    <Modal
      title={t("contractTemplates.title")}
      open={isOpenCreateModal || isOpenEditModal}
      onCancel={closeModal}
      closable
      centered
      destroyOnHidden
      okButton={{
        htmlType: "submit",
        form: formId,
        children: t("ui.save"),
      }}
    >
      <ContractTemplatesForm
        currentItem={isOpenEditModal ? currentContract : undefined}
        onSubmit={onSubmit}
        formId={formId}
      />
    </Modal>
  );
};

export default ContractTemplatesModal;
